import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiBuilding2Line, RiRobot2Line, RiSettings3Line, RiUserAddLine } from '@remixicon/react'
import type { WorkspaceListItem } from 'shared'
import { useAuth } from '../../contexts/AuthContext'
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarLink,
  SidebarCollapsible,
  SidebarSubLink,
  Divider,
} from '../ui/Sidebar'
import AccountMenu from './AccountMenu'
import AppVersion from '../AppVersion'
import WorkspaceSwitcher from './WorkspaceSwitcher'
import { cx, focusRing } from '../../lib/utils'
import Logo from '../ui/Logo'

interface OperatorSidebarProps {
  workspaces: WorkspaceListItem[]
  /** Called when the switcher finds itself looking at a company it has never heard of. */
  onWorkspacesStale: () => void
}

export default function OperatorSidebar({ workspaces, onWorkspacesStale }: OperatorSidebarProps) {
  const { t } = useTranslation()
  const { user } = useAuth()

  return (
    <Sidebar>
      <SidebarHeader>
        <Link to="/app/workspaces" className="flex items-center gap-3">
          <div>
            <Logo />
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {t('nav.operatorConsole')}
            </span>
          </div>
        </Link>
      </SidebarHeader>

      {/* Where you are, and the way to anywhere else — above the sections,
          because in this console every section is read in the context of one
          company. */}
      <div className="px-3 pb-2">
        <WorkspaceSwitcher workspaces={workspaces} onStale={onWorkspacesStale} />
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              <SidebarMenuItem>
                <SidebarLink href="/app/workspaces" icon={RiBuilding2Line}>
                  {t('nav.workspaces')}
                </SidebarLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarLink href="/app/onboarding" icon={RiUserAddLine}>
                  {t('nav.onboarding')}
                </SidebarLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarLink href="/app/agents" icon={RiRobot2Line}>
                  {t('nav.agents')}
                </SidebarLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {user?.role === 'admin' && (
          <>
            <div className="px-3">
              <Divider className="my-0 py-0" />
            </div>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-4">
                  <SidebarMenuItem>
                    <SidebarCollapsible
                      name={t('nav.administration')}
                      icon={RiSettings3Line}
                      defaultOpen
                    >
                      <SidebarSubLink href="/app/templates">
                        {t('nav.surveyTemplates')}
                      </SidebarSubLink>
                      <SidebarSubLink href="/app/users">{t('nav.users')}</SidebarSubLink>
                      <SidebarSubLink href="/app/feedback">{t('admin.feedback')}</SidebarSubLink>
                      <SidebarSubLink href="/app/notifications">
                        {t('admin.notifications')}
                      </SidebarSubLink>
                      <SidebarSubLink href="/app/privacy">{t('nav.privacyNotices')}</SidebarSubLink>
                      <SidebarSubLink href="/app/announcements">{t('nav.announcements')}</SidebarSubLink>
                    </SidebarCollapsible>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <div className="border-t border-gray-200 dark:border-gray-800" />
        <AccountMenu
          align="start"
          className="w-[var(--radix-dropdown-menu-trigger-width)]"
          trigger={
            <button
              className={cx(
                'flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-gray-200/50 dark:hover:bg-gray-900',
                focusRing,
              )}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
              <span className="flex flex-1 flex-col truncate">
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                  {user?.name}
                </span>
                <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {t(`admin.role_${user?.role}`)}
                </span>
              </span>
            </button>
          }
        />
        <AppVersion className="px-2" />
      </SidebarFooter>
    </Sidebar>
  )
}
